/**
 * Turkish number and time formats used across the panel (spec 2.4): "516,24 $", "%85",
 * "3.837", "07:41", "6 dk", "10 sa", "1 dk 06 sn", "27 Eyl". Times are always shown in
 * Europe/Istanbul, whatever the device time zone is. Browser-safe on purpose: the shared
 * package also reads config files from disk, so it is not bundled into the panel.
 */

import type { Instant, MicroUsd } from '../api/types.js';

export const TIME_ZONE = 'Europe/Istanbul';

export const MONTHS = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
] as const;

export const MONTHS_SHORT = [
  'Oca',
  'Şub',
  'Mar',
  'Nis',
  'May',
  'Haz',
  'Tem',
  'Ağu',
  'Eyl',
  'Eki',
  'Kas',
  'Ara',
] as const;

/** Index 0 is Sunday. */
export const WEEKDAYS = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'] as const;
export const WEEKDAYS_SHORT = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'] as const;

const MICRO = 1_000_000;

/** Groups digits Turkish style: "1.234.567". */
export function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** "3.837", "4,94" with decimals. */
export function formatNumber(value: number, decimals = 0): string {
  const fixed = Math.abs(value).toFixed(decimals);
  const [whole = '0', fraction] = fixed.split('.');
  const sign = value < 0 && Number(fixed) !== 0 ? '-' : '';
  return `${sign}${groupThousands(whole)}${fraction === undefined ? '' : `,${fraction}`}`;
}

let currencySuffix = '$';

/**
 * Para birimi gösterimi (Ayarlar, Tercihler): "516,24 $" or "516,24 USD". A display
 * preference only; amounts stay micro-USD everywhere.
 */
export function setCurrencyDisplay(display: 'symbol' | 'code'): void {
  currencySuffix = display === 'code' ? 'USD' : '$';
}

/** Micro-USD to "516,24 $". With decimals 'auto', whole amounts drop the cents: "55 $". */
export function formatUsd(amount: MicroUsd, decimals: number | 'auto' = 2): string {
  return `${formatUsdNumber(amount, decimals)} ${currencySuffix}`;
}

/** Micro-USD to "516,24" without the currency sign. */
export function formatUsdNumber(amount: MicroUsd, decimals: number | 'auto' = 2): string {
  const places = decimals === 'auto' ? (amount % MICRO === 0 ? 0 : 2) : decimals;
  const scale = 10 ** places;
  const rounded = Math.round((Math.abs(amount) / MICRO) * scale) / scale;
  return formatNumber(amount < 0 ? -rounded : rounded, places);
}

/** "11,90 / 55 $": spent against a cap, the cap without cents when it is whole. */
export function formatUsdPair(spent: MicroUsd, cap: MicroUsd): string {
  return `${formatUsdNumber(spent)} / ${formatUsdNumber(cap, 'auto')} ${currencySuffix}`;
}

/** A ratio as "%85". */
export function formatPct(ratio: number, decimals = 0): string {
  return `%${formatNumber(ratio * 100, decimals)}`;
}

/** A 0 to 100 value as "%85". */
export function formatPctValue(value: number, decimals = 0): string {
  return `%${formatNumber(value, decimals)}`;
}

export interface WallParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  weekday: number;
}

let partsFormatter: Intl.DateTimeFormat | null = null;

/** Wall clock parts of an instant in Istanbul. */
export function wallParts(at: Instant): WallParts {
  partsFormatter ??= new Intl.DateTimeFormat('en-US', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23',
  });
  const values: Record<string, number> = {};
  for (const part of partsFormatter.formatToParts(new Date(at))) {
    if (part.type !== 'literal') values[part.type] = Number(part.value);
  }
  const year = values.year ?? 1970;
  const month = values.month ?? 1;
  const day = values.day ?? 1;
  return {
    year,
    month,
    day,
    hour: values.hour ?? 0,
    minute: values.minute ?? 0,
    second: values.second ?? 0,
    weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
  };
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** "07:41" */
export function formatClock(at: Instant): string {
  const p = wallParts(at);
  return `${pad2(p.hour)}:${pad2(p.minute)}`;
}

/** "07:40:06" */
export function formatClockSeconds(at: Instant): string {
  const p = wallParts(at);
  return `${pad2(p.hour)}:${pad2(p.minute)}:${pad2(p.second)}`;
}

function dayNumber(at: Instant): number {
  const p = wallParts(at);
  return Date.UTC(p.year, p.month - 1, p.day) / 86_400_000;
}

/** "07:34", "dün 21:48", "Pzt 09:00" or "24 Eyl 14:20" relative to now. */
export function formatRelativeClock(at: Instant, now: Instant): string {
  const diff = dayNumber(at) - dayNumber(now);
  if (diff === 0) return formatClock(at);
  if (diff === -1) return `dün ${formatClock(at)}`;
  if (diff > 0 && diff < 7) return `${WEEKDAYS_SHORT[wallParts(at).weekday]} ${formatClock(at)}`;
  return `${formatDayMonth(at)} ${formatClock(at)}`;
}

/** "Dün 18:04" style for list rows. */
export function formatRelativeDay(at: Instant, now: Instant): string {
  const text = formatRelativeClock(at, now);
  return text.charAt(0).toLocaleUpperCase('tr-TR') + text.slice(1);
}

/** "27 Eyl" */
export function formatDayMonth(at: Instant): string {
  const p = wallParts(at);
  return `${p.day} ${MONTHS_SHORT[p.month - 1]}`;
}

/** The Europe/Istanbul calendar day of an instant as YYYY-MM-DD (the API's IsoDate). */
export function istanbulIsoDate(at: Instant): string {
  const p = wallParts(at);
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

/** The Istanbul day `days` days after the day of `at`, as YYYY-MM-DD. */
export function addIsoDays(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days));
  return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
}

/** "3 Eki" for the IsoDate "2026-10-03"; anything that is not YYYY-MM-DD is shown as is. */
export function formatIsoDay(isoDate: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return isoDate;
  const month = Number(match[2]);
  const label = MONTHS_SHORT[month - 1];
  return label ? `${Number(match[3])} ${label}` : isoDate;
}

/** "Pazar, 27 Eylül" or with the year "Pazar, 27 Eylül 2026". */
export function formatLongDate(at: Instant, withYear = false): string {
  const p = wallParts(at);
  return `${WEEKDAYS[p.weekday]}, ${p.day} ${MONTHS[p.month - 1]}${withYear ? ` ${p.year}` : ''}`;
}

/**
 * How long something has waited: "6 dk" under an hour (whole minutes), "10 sa" from an hour
 * on (nearest hour), "2 gün" from a day on.
 */
export function formatAge(ms: number): string {
  const minutes = Math.max(0, Math.floor(ms / 60_000));
  if (minutes < 60) return `${minutes} dk`;
  const hours = Math.round(ms / 3_600_000);
  if (hours < 24) return `${hours} sa`;
  return `${Math.round(ms / 86_400_000)} gün`;
}

/** Elapsed time: "41 sn", "1 dk 06 sn", "10 sa 19 dk". */
export function formatDuration(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  if (seconds < 60) return `${seconds} sn`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} dk ${pad2(seconds % 60)} sn`;
  const hours = Math.floor(minutes / 60);
  return `${hours} sa ${minutes % 60} dk`;
}

/** Run log delta: "+3 sn", "+41 sn", "+1 dk 06 sn". */
export function formatDelta(seconds: number): string {
  return `+${formatDuration(seconds)}`;
}

/** Seconds with one decimal, Turkish style: "1,2 sn". */
export function formatSecondsLeft(ms: number): string {
  return `${formatNumber(Math.max(0, ms) / 1000, 1)} sn`;
}

/** Month progress as a ratio: day 27 of a 30-day month is 0.9. */
export function monthProgress(at: Instant): number {
  const p = wallParts(at);
  const daysInMonth = new Date(Date.UTC(p.year, p.month, 0)).getUTCDate();
  return p.day / daysInMonth;
}

/** Budget pace: ahead when spent% is more than 10 points over month-elapsed%. */
export function budgetPace(spentRatio: number, elapsedRatio: number): 'ok' | 'ahead' | 'over' {
  if (spentRatio >= 1) return 'over';
  if (spentRatio * 100 > elapsedRatio * 100 + 10) return 'ahead';
  return 'ok';
}

/** Time until something, minutes rounded up: 18:00 seen from 07:41:12 is "10 sa 19 dk". */
export function formatUntil(ms: number): string {
  const minutes = Math.max(0, Math.ceil(ms / 60_000));
  if (minutes < 60) return `${minutes} dk`;
  return `${Math.floor(minutes / 60)} sa ${minutes % 60} dk`;
}

// Ablative suffix after a spoken time ("07:40'tan", "07:39'dan", "18:00'den"): it follows
// the last spoken word, so vowel harmony and consonant hardening come from that word.
const UNIT_SUFFIX = ['dan', 'den', 'den', 'ten', 'ten', 'ten', 'dan', 'den', 'den', 'dan'] as const;
const TEN_SUFFIX = ['dan', 'dan', 'den', 'dan', 'tan', 'den'] as const;

function spokenSuffix(value: number): string {
  if (value === 0) return 'dan';
  const unit = value % 10;
  if (unit !== 0) return UNIT_SUFFIX[unit] ?? 'den';
  return TEN_SUFFIX[Math.floor(value / 10)] ?? 'dan';
}

/** "07:40'tan beri" */
export function formatSince(at: Instant): string {
  const p = wallParts(at);
  const suffix = spokenSuffix(p.minute === 0 ? p.hour : p.minute);
  return `${formatClock(at)}'${suffix} beri`;
}

/** Status note of an accepted request: "Teslim bekleniyor · hedef 3 Eki". */
export function acceptedRequestNote(targetDate: string): string {
  return `Teslim bekleniyor · hedef ${formatIsoDay(targetDate)}`;
}

/** Status note of a postponed request: "4 Eki tarihine ertelendi". */
export function deferredRequestNote(until: string): string {
  return `${formatIsoDay(until)} tarihine ertelendi`;
}

/** A Claude quota window percentage, or "veri yok" before the first quota snapshot (2.10 row 19). */
export function formatQuotaPct(value: number | null): string {
  return value === null ? 'veri yok' : formatPctValue(value);
}
